import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message, notification, Space } from "antd";
import { processDossierAsDealership } from "../../../../lib/api/resources/dossiers";
import { Button, Card } from "@rumbledirect/ui";
import { SendToServicePartnerAction } from "./send-to-service-partner-action";
import { GenerateDamageIntakeReportAction } from "./generate-damage-intake-report-action";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";

interface DossierActionsCardProps {
  dossier: Dossier | undefined;
  isLoading?: boolean;
}

interface processMutationParams {
  processType: "gate-in" | "notify-driver" | "gate-out";
  successMessage: string;
  successDescription?: string;
}

export const DossierActionsCard = ({
  dossier,
  isLoading,
}: DossierActionsCardProps) => {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const hasSendToServicePartnerPermission = currentUser?.permissions?.includes(
    "admin_dossier_send_to_service_partner",
  );
  const hasGenerateDamageIntakeReportPermission =
    currentUser?.permissions?.includes(
      "admin_dossier_damage_intake_report_generate",
    );
  const processMutation = useMutation({
    mutationFn: ({ processType }: processMutationParams) => {
      if (!dossier) {
        throw new Error("No dossier provided");
      }

      return processDossierAsDealership({
        dossierIri: dossier["@id"],
        processType,
      });
    },
    onSuccess: (_data, variables) => {
      notification.success({
        message: variables.successMessage,
        description: variables.successDescription,
      });
      queryClient.resetQueries();
    },
    onError: () => {
      message.error("Er is iets misgegaan.");
    },
  });
  return (
    <Card title="Acties" loading={isLoading} className="w-full mb-5">
      {/* TODO: This needs to be made dynamic asap! */}
      <Space direction="vertical" className="w-full">
        {dossier?.status === "kimman_dossier_aangemaakt" && (
          <Button
            type="primary"
            size="large"
            className="w-full"
            loading={processMutation.isPending}
            onClick={() =>
              processMutation.mutate({
                processType: "gate-in",
                successMessage: "Voertuig binnen gemeld",
                successDescription:
                  "Er is een mail verstuurd naar Schadenet RECO dat het voertuig klaar staat voor transport.",
              })
            }
          >
            Voertuig binnen melden
          </Button>
        )}
        {dossier?.status === "voertuig_klaar_bij_reco" && (
          <Button
            type="primary"
            size="large"
            className="w-full"
            loading={processMutation.isPending}
            onClick={() =>
              processMutation.mutate({
                processType: "notify-driver",
                successMessage: "Voertuig binnen gemeld",
                successDescription:
                  "Er is een mail verstuurd naar de bestuurder dat het voertuig klaar staat om opgehaald te worden.",
              })
            }
          >
            Voertuig binnen melden
          </Button>
        )}
        {dossier?.status === "voertuig_terug_bij_kimman" && (
          <Button
            type="primary"
            size="large"
            className="w-full"
            loading={processMutation.isPending}
            onClick={() =>
              processMutation.mutate({
                processType: "gate-out",
                successMessage: "Voertuig afgemeld",
              })
            }
          >
            Voertuig afmelden
          </Button>
        )}
        {hasSendToServicePartnerPermission && (
          <SendToServicePartnerAction dossier={dossier} />
        )}
        {hasGenerateDamageIntakeReportPermission && (
          <GenerateDamageIntakeReportAction dossier={dossier} />
        )}
      </Space>
    </Card>
  );
};
